<template>
  <div>
    <Select
      v-if="canEdit"
      :model-value="modelValue"
      @update:model-value="emit('update:modelValue', $event ? String($event) : null)"
      :disabled="disabled"
    >
      <SelectTrigger class="w-full max-w-[400px] pl-6">
        <SelectValue :placeholder="placeholder" class="text-black font-medium">
          <div v-if="selectedMainGuide" class="flex items-center">
            <img
              v-if="selectedMainGuide.avatar_image"
              :src="selectedMainGuide.avatar_image"
              class="w-5 h-5 rounded-full mr-2 object-cover"
            />
            <div v-else class="w-5 h-5 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
              <UserRoundIcon class="w-3 h-3 text-gray-600" />
            </div>
            {{ selectedMainGuide.first_name }} {{ selectedMainGuide.last_name }}
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="guide in availableGuides" :key="guide.id" :value="String(guide.id)">
          <div class="flex items-center">
            <img
              v-if="guide.avatar_image"
              :src="guide.avatar_image"
              class="w-5 h-5 rounded-full mr-2 object-cover"
            />
            <div v-else class="w-5 h-5 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
              <UserRoundIcon class="w-3 h-3 text-gray-600" />
            </div>
            {{ guide.first_name }} {{ guide.last_name }}
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
    <div v-else class="w-full max-w-[400px]">
      <div class="flex items-center rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700">
        <template v-if="selectedMainGuide">
          <img
            v-if="selectedMainGuide.avatar_image"
            :src="selectedMainGuide.avatar_image"
            class="w-5 h-5 rounded-full mr-2 object-cover"
          />
          <div v-else class="w-5 h-5 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
            <UserRoundIcon class="w-3 h-3 text-gray-600" />
          </div>
          {{ selectedMainGuide.first_name }} {{ selectedMainGuide.last_name }}
        </template>
        <span v-else class="text-gray-500">No {{ label.toLowerCase() }} selected.</span>
      </div>
    </div>
    <p v-if="availableGuides.length === 0" class="text-sm text-gray-500 mt-2">
      No guides available.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import type { User } from '@/types/user'
import { UserRoundIcon } from 'lucide-vue-next'
import { useUsersStore } from '@/stores/users'
import { storeToRefs } from 'pinia'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useAuth } from '@/composables/useAuth'

const { isAdmin } = useAuth()
const usersStore = useUsersStore()
const { guides: storeGuides } = storeToRefs(usersStore)

const props = withDefaults(
  defineProps<{
    modelValue: string | null
    guides: User[]
    disabled?: boolean
    canEdit?: boolean
    label?: string
    placeholder?: string
  }>(),
  {
    disabled: false,
    canEdit: undefined,
    guides: () => [],
    label: 'Main guide',
    placeholder: 'Select main guide',
  }
)

const canEdit = computed(() => props.canEdit ?? isAdmin.value)

const emit = defineEmits(['update:modelValue'])

onMounted(async () => {
  if (storeGuides.value.length === 0) {
    try {
      await usersStore.fetchGuides()
    } catch (error) {
      console.error('Error fetching guides:', error)
    }
  }
})

const availableGuides = computed(() => storeGuides.value)

const selectedGuidePool = computed(() => {
  return storeGuides.value.length > 0 ? storeGuides.value : props.guides
})

const selectedMainGuide = computed(() => {
  if (props.modelValue === null) return undefined
  return selectedGuidePool.value.find((guide) => String(guide.id) === props.modelValue)
})
</script>